/**
 * Push notifications: one service worker (sw.js) serves the PWA shell and renders pushes. The FCM token
 * is stored under users/{uid}/pushTokens/{hash} so the server can reach every device the person enabled.
 */
import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { doc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { app, auth, db, usingEmulators } from "./firebase";

const VAPID = (import.meta.env.VITE_FCM_VAPID_KEY as string | undefined) ?? "";
const LS = "timo-push-token";

export const pushSupported = async () => !usingEmulators && !!VAPID && "Notification" in window && "serviceWorker" in navigator && (await isSupported().catch(() => false));
export const pushState = (): "granted" | "denied" | "default" | "unsupported" => ("Notification" in window ? Notification.permission : "unsupported");
export const pushEnabled = () => pushState() === "granted" && !!localStorage.getItem(LS);

async function hash(s: string) { const b = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s)); return Array.from(new Uint8Array(b)).map((x) => x.toString(16).padStart(2, "0")).join("").slice(0, 32); }

/** Ask for permission (must be called from a click) and register this device. */
export async function enablePush(): Promise<{ ok: boolean; reason?: string }> {
  if (!(await pushSupported())) return { ok: false, reason: usingEmulators ? "Push is disabled on the emulator" : "This browser doesn't support push. On iPhone, add Timo to the Home Screen first." };
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: perm === "denied" ? "Notifications are blocked for timonow.com in your browser settings" : "Permission not granted" };
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  const token = await getToken(getMessaging(app), { vapidKey: VAPID, serviceWorkerRegistration: reg });
  const uid = auth.currentUser?.uid; if (!uid || !token) return { ok: false, reason: "Couldn't register this device" };
  await setDoc(doc(db, `users/${uid}/pushTokens/${await hash(token)}`), { token, ua: navigator.userAgent.slice(0, 120), platform: matchMedia("(display-mode: standalone)").matches ? "pwa" : "web", createdAt: serverTimestamp(), lastSeen: serverTimestamp() }, { merge: true });
  localStorage.setItem(LS, token);
  return { ok: true };
}

export async function disablePush() {
  const token = localStorage.getItem(LS); const uid = auth.currentUser?.uid;
  if (token && uid) await deleteDoc(doc(db, `users/${uid}/pushTokens/${await hash(token)}`)).catch(() => {});
  localStorage.removeItem(LS);
}

/** Keep the stored token fresh on each visit (FCM rotates them occasionally). */
export async function refreshPushToken() {
  if (!pushEnabled() || !(await pushSupported())) return;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js");
    const token = await getToken(getMessaging(app), { vapidKey: VAPID, serviceWorkerRegistration: reg });
    const uid = auth.currentUser?.uid; if (!uid || !token) return;
    if (token !== localStorage.getItem(LS)) { await disablePush(); localStorage.setItem(LS, token); }
    await setDoc(doc(db, `users/${uid}/pushTokens/${await hash(token)}`), { token, lastSeen: serverTimestamp() }, { merge: true });
  } catch { /* offline or permission revoked. The server prunes dead tokens */ }
}
